import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { v4 as uuid } from "uuid";
import { AgBytesAPI, HierarchyAPI, OrgLevelProductsAPI } from "@ai360/core";

import RestrictedProductList from "~/utils/products/restricted-product-list";
import { messages } from "~/admin/setup/i18n-messages";
import {
    IProductDropdownOption,
    toProductDropdownOptions,
    useProductList,
} from "~/utils/products/use-product-list";
import { Button, Loader, LoaderTypes, Section, SelectInput, SubSection } from "~/core";
import SlidingPanel from "~/sliding-panel/sliding-panel";

import { OrgLevelList } from "./components/org-level-list";
import { ButtonTypes } from "~/core/components/button/button";

type IHierarchyFilterLevelWithParentsGuid = HierarchyAPI.IHierarchyFilterLevelWithParentsGuid;
type IOrgLevelProductProduct = OrgLevelProductsAPI.IOrgLevelProductProduct;
type IUserCompanyList = OrgLevelProductsAPI.IUserCompanyList;

type OrgLevelProductsAddEditPanelProps = {
    closePanel: () => void;
    intl: intlShape;
    selectedRow: OrgLevelProductsAPI.IOrgLevelProductGridRow | null;
};

type OrgLevelProductsPanelBodyData = {
    companies: IUserCompanyList[];
    formatMessage: (descriptor: unknown, values?: Record<string, unknown>) => string;
    isLoading: boolean;
    isProductLoading: boolean;
    locationOptions: IHierarchyFilterLevelWithParentsGuid[];
    productOptions: IProductDropdownOption[];
    selectedCompanyUserGuid: string | null;
    selectedLocations: IHierarchyFilterLevelWithParentsGuid[];
    selectedProducts: IOrgLevelProductProduct[];
    setHasUnlinkedProduct: (hasUnlinkedProduct: boolean) => void;
    setSelectedCompanyUserGuid: (userGuid: string | null) => void;
    setSelectedLocations: (locations: IHierarchyFilterLevelWithParentsGuid[]) => void;
    setSelectedProducts: (products: IOrgLevelProductProduct[]) => void;
};

/**
 * Declared at module scope on purpose. SlidingPanel renders `component` through
 * React.createElement, so a body rebuilt inside the panel's render is a new element type every
 * time and React unmounts and remounts the whole subtree on any panel state change. That remount
 * refetches the Agvance restriction, which changes panel state, which remounts again -- an endless
 * loop. Everything the body needs arrives through SlidingPanel's `data` prop, which it spreads.
 */
const OrgLevelProductsPanelBody = ({ data }: { data: OrgLevelProductsPanelBodyData }) => {
    const {
        companies,
        formatMessage,
        isLoading,
        isProductLoading,
        locationOptions,
        productOptions,
        selectedCompanyUserGuid,
        selectedLocations,
        selectedProducts,
        setHasUnlinkedProduct,
        setSelectedCompanyUserGuid,
        setSelectedLocations,
        setSelectedProducts,
    } = data;

    return (
        <div className="add-edit-panel org-level-products-add-edit">
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
            <div className="section-container">
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.productsHeader)}
                    testId="org-level-products-products-section"
                >
                    <SubSection testId="org-level-products-product-list-subsection">
                        <RestrictedProductList
                            formatMessage={formatMessage}
                            options={productOptions}
                            record={selectedProducts}
                            onTextChange={(e, value) => {
                                setSelectedProducts([...value]);
                            }}
                            onUnlinkedProductsChange={setHasUnlinkedProduct}
                            isProductsFetching={isProductLoading}
                            showInactiveProducts={true}
                        />
                    </SubSection>
                </Section>
                <span className="bar section-fence" />
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.orgLevelList)}
                    testId="org-level-products-org-level-list-section"
                >
                    <SubSection
                        className="select-company-container"
                        testId="org-level-products-select-company-subsection"
                    >
                        <div className="select-company-message">
                            {formatMessage(messages.selectACompanyMessage)}
                        </div>
                        <SelectInput
                            clearable={false}
                            onChange={setSelectedCompanyUserGuid}
                            options={companies.map(({ userGuid, companyName }) => ({
                                value: userGuid,
                                label: companyName,
                            }))}
                            placeholderText={formatMessage(messages.companyName)}
                            required
                            value={selectedCompanyUserGuid}
                            testId="org-level-products-company-name-select"
                        />
                    </SubSection>
                    <OrgLevelList
                        orgLevels={locationOptions}
                        onSelectionChange={setSelectedLocations}
                        selectedOrgLevels={selectedLocations}
                    />
                </Section>
            </div>
        </div>
    );
};

const OrgLevelProductsAddEditPanel_ = ({
    closePanel,
    intl: { formatMessage },
    selectedRow,
}: OrgLevelProductsAddEditPanelProps): ReactElement => {
    const [selectedCompanyUserGuid, setSelectedCompanyUserGuid] = useState(null as string | null);
    const [selectedLocations, setSelectedLocations] = useState(
        [] as IHierarchyFilterLevelWithParentsGuid[]
    );
    const [selectedProducts, setSelectedProducts] = useState([] as IOrgLevelProductProduct[]);
    const [hasUnlinkedProduct, setHasUnlinkedProduct] = useState(false);

    const [mergeOrgLevelProduct, { isLoading: isMergeLoading }] =
        OrgLevelProductsAPI.useMergeOrgLevelProductMutation();

    const {
        data: companyData = { model: [] as IUserCompanyList[] },
        isFetching: isCompanyLoading,
    } = OrgLevelProductsAPI.useGetUserCompanyListQuery();

    const [lazyLoadCompany, { isFetching: isLazyCompanyLoading }] =
        OrgLevelProductsAPI.useLazyGetOrgLevelListWithParentsGuidQuery();

    const {
        data: locationData = { model: [] as IHierarchyFilterLevelWithParentsGuid[] },
        isFetching: isLocationLoading,
    } = OrgLevelProductsAPI.useGetOrgLevelListWithParentsGuidQuery(selectedCompanyUserGuid, {
        skip: !selectedCompanyUserGuid,
    });

    // Full list on open; once locations are picked, each location's linked-only setting applies.
    const { products, isFetching: isProductLoading } = useProductList({
        orgLevelGuids:
            selectedLocations.length > 0
                ? selectedLocations.map((location) => location.orgLevelGuid)
                : undefined,
        scope: AgBytesAPI.ProductSelectionListScope.Catalog,
    });
    const productOptions = useMemo(() => toProductDropdownOptions(products), [products]);

    useEffect(() => {
        if (!selectedRow || companyData.model.length === 0) return;
        setSelectedProducts(selectedRow.products.map((p) => ({ ...p })));

        const orgLevelGuids = new Set(selectedRow.orgLevels.map((l) => l.orgLevelGuid));
        const parentOrgLevels = new Set(selectedRow.orgLevels.map((l) => l.parentOrgLevelGuid));
        const companyUserGuids = Array.from(
            new Set(
                companyData.model
                    .filter((c) => parentOrgLevels.has(c.companyGuid))
                    .map((c) => c.userGuid)
            )
        );
        if (companyUserGuids.length === 1) {
            setSelectedCompanyUserGuid(companyUserGuids[0]);
        }
        const results = companyUserGuids.map((userGuid) => {
            return lazyLoadCompany(userGuid);
        });
        Promise.all(results).then((values) => {
            const locations = values
                .map((value) => value.data.model)
                .flat()
                .filter((location) => orgLevelGuids.has(location.orgLevelGuid))
                .map((location) => ({ ...location }));
            setSelectedLocations(locations);
        });
    }, [selectedRow, companyData]);

    const mutableLocationData = useMemo(() => {
        return locationData.model.map((location) => ({ ...location }));
    }, [locationData]);

    const locationOptions = useMemo(() => {
        const locationGuids = new Set<string>(
            mutableLocationData.map((location) => location.orgLevelGuid)
        );
        const missingFromSelected = selectedLocations.filter((location) => {
            return !locationGuids.has(location.orgLevelGuid);
        });
        return [...missingFromSelected, ...mutableLocationData];
    }, [selectedLocations, mutableLocationData]);

    const isLoading =
        isCompanyLoading ||
        isLocationLoading ||
        isProductLoading ||
        isMergeLoading ||
        isLazyCompanyLoading;

    const onSave = () => {
        const mergeModel = {
            productOrgLevelGuid: selectedRow ? selectedRow.productOrgLevelGuid : uuid(),
            active: true,
            products: selectedProducts.map((product) => ({
                productGuid: product.productGuid,
                name: product.name,
            })),
            orgLevels: selectedLocations.map((location) => ({
                orgLevelGuid: location.orgLevelGuid,
                name: location.orgLevelName,
                parentOrgLevelGuid:
                    location.orgLevelParents.length > 0 ? location.orgLevelParents[0].guid : null,
            })),
        };

        mergeOrgLevelProduct(mergeModel)
            .unwrap()
            .then(() => {
                closePanel();
            })
            .catch(() => {
                // Handled by ApiResultsWrapper
            });
    };

    const panelBodyData: OrgLevelProductsPanelBodyData = {
        companies: companyData.model,
        formatMessage,
        isLoading,
        isProductLoading,
        locationOptions,
        productOptions,
        selectedCompanyUserGuid,
        selectedLocations,
        selectedProducts,
        setHasUnlinkedProduct,
        setSelectedCompanyUserGuid,
        setSelectedLocations,
        setSelectedProducts,
    };

    return (
        <SlidingPanel
            component={OrgLevelProductsPanelBody}
            data={panelBodyData}
            navigateTo={{ parentNameCode: "101", childNameCode: "274" }}
        >
            <Button
                type={ButtonTypes.button}
                value={formatMessage(messages.save)}
                forceSubmit
                disabled={
                    selectedProducts.length === 0 ||
                    selectedLocations.length === 0 ||
                    hasUnlinkedProduct
                }
                onClick={onSave}
                testId="org-level-products-save-button"
            />
            <Button
                type={ButtonTypes.cancel}
                className="user-cancel"
                onClick={closePanel}
                testId="org-level-products-cancel-button"
            />
        </SlidingPanel>
    );
};

export const OrgLevelProductsAddEditPanel = injectIntl(OrgLevelProductsAddEditPanel_);
